import Button from '../../../../Button/Button';
import Icon from '../../../../Icon2/Icon';
import Info from '../../../../Info/Info';
import Pills from '../../../../Pills/Pill';
import Select from '../../../../Select/Select';
import TextInput from '../../../../TextInput/TextInput';
import React, { useEffect } from 'react'
import './index.scss';
import { debounce } from 'lodash';
import { FIELDS_LIST_FILTERS_OPTIONS, FILTER_OPERATORS } from '../../../utils/constants';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useQueryBuilder } from '../context/QueryBuilderContext';

interface FieldsListProps {
  setFilters: (filters: any) => void;
  filters: any;
  closeDropdown: () => void;
  defaultFields?: any[];
  linkedSpaces?: string[];
  fetchFieldsAMv2: (query: any, params: any, headers: any, options: any) => Promise<any>;
  
}

type PillItem = {
  text: string;
  id: string;
  display_type: string;
  isSystem?: boolean;
  isSelected: boolean;
  isDefault?: boolean;
};

type selectedFields = {
  field: string;
  operator: string;
  value: string;
  id: string;
  type: string;
  isSelected: boolean;
}

const FieldsList: React.FC<FieldsListProps> = ({ closeDropdown, setFilters, filters, defaultFields, linkedSpaces, fetchFieldsAMv2 }) => {
  const [allPillItems, setAllPillItems] = React.useState<PillItem[]>([]);
  const [pillItems, setPillItems] = React.useState<PillItem[]>([]);
  const [searchText, setSearchText] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  const [selectedFields, setSelectedFields] = React.useState<selectedFields[]>([]);
  const [skip, setSkip] = React.useState(0);
  const limit = 40; // Number of items to fetch per call
  const [hasMore, setHasMore] = React.useState(true);

  const {
    removeMultipleConditions,
  } = useQueryBuilder();

  const [filterView, setFilterView] = React.useState(FIELDS_LIST_FILTERS_OPTIONS[0]);

  const handleChange = (event) => {
    const value = event.target.value.trim();

    setSearchText(value);
    setSkip(0);
    setHasMore(true);
    if (filterView.value !== 'SELECTED') {
      setPillItems([]);
      setAllPillItems([]);
      debouncedFetch(value, filterView.value);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !loading) {
      const newSkip = skip + limit;
      setSkip(newSkip);
      fetchFields(searchText, newSkip, filterView.value);
    }
  };
  const applySelectedFields = () => {
    setFilters((prev: any) => {
      const newFilters = selectedFields
        .filter((sf) => !prev.some((f) => f.id === sf.id))
        .map(({ isSelected, ...rest }) => rest); // remove isSelected

      const remainingFilters = prev.filter((f) =>
        selectedFields.some((sf) => sf.id === f.id)
      );

      const finalSelections = [...remainingFilters, ...newFilters];

      const removedFieldIds = prev
        .filter((f) => !selectedFields.some((sf) => sf.id === f.id))
        .map((f) => f.id);

      if (removedFieldIds.length > 0) {
        const defaultConditions: Record<string, any> = {};

        if (removedFieldIds.includes('space_uid')) {
          defaultConditions.space_uid = { [FILTER_OPERATORS.IN]: linkedSpaces };
        }

        removeMultipleConditions(removedFieldIds, defaultConditions);
      }

      return finalSelections;
    });

    closeDropdown();
  };

  const clearAll = () => {
    setSelectedFields([]);
  };
  const debouncedFetch = React.useCallback(
    debounce((searchText, filter) => {
      fetchFields(searchText, 0, filter);
    }, 300),
    []
  );
  const fetchFields = async (search_text?: string, skipVal = 0, filter: any = filterView.value) => {
    if (loading || (skipVal > 0 && !hasMore)) return;

    try {
      setLoading(true);
      const query: any = { skip: skipVal, limit };
      if (search_text) query.search_text = search_text;
      if (filter !== 'ALL') {
        query.display_type = filter === 'USER' ? filter = 'ALL' : filter;
      }
      const res = await fetchFieldsAMv2(query, {}, {}, {});
      const fetchedFields: any[] = res?.fields || [];



      let newPills = fetchedFields.map((field) => ({
        text: field.title,
        id: field.uid,
        display_type: field.display_type,
        isSelected: selectedFields.some((f) => f.id === field.uid),
      }));

      if (skipVal === 0 && (defaultFields?.length ?? 0) > 0) {
        const defaultPills = defaultFields?.map((df) => ({
          text: df.field,
          id: df.id || df.field,
          display_type: df.type || 'text',
          isSelected: selectedFields.some((f) => f.id === (df.id || df.field)),
          isDefault: true,
        }));

        const uniqueMap = new Map<string, any>();
        [...(defaultPills || []), ...newPills].forEach((pill) => {
          if (!uniqueMap.has(pill.id)) {
            uniqueMap.set(pill.id, pill);
          }
        });
        const dedupedPills = Array.from(uniqueMap.values());
        const nonDefaultSorted = dedupedPills
          .filter((pill) => !pill.isDefault)
          .sort((a, b) => a.text.localeCompare(b.text));

        newPills = [...defaultPills!, ...nonDefaultSorted];
      }

      if (skipVal === 0) {
        setAllPillItems(newPills);
        setPillItems(newPills);
      } else {
        setAllPillItems((prev) => [...prev, ...newPills]);
        setPillItems((prev) => [...prev, ...newPills]);
      }

      if (fetchedFields.length < limit) setHasMore(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const togglePillSelection = (item) => {
    setSelectedFields((prev) => {
      const exists = prev.find((f) => f.id === item.itemId);
      if (exists) {
        return prev.filter((f) => f.id !== item.itemId);
      } else {
        const matchingPill = pillItems.find((pill) => pill.id === item.itemId);
        return [
          ...prev,
          {
            field: item.text,
            operator: '',
            value: '',
            id: item.itemId,
            type: matchingPill?.display_type || 'text',
            isSelected: true,
          },
        ];
      }
    });
  };

  useEffect(() => {
    if (filters?.length > 0) {
      const alreadySelected = filters.map((f) => ({
        ...f,
        isSelected: true,
      }));
      setSelectedFields(alreadySelected);
    }
  }, [filters]);

  useEffect(() => {
    fetchFields('', 0, filterView.value);
  }, []);

  const handleViewchange = (selectedOption) => {
    setFilterView(selectedOption);

    setSearchText('');
    setSkip(0);
    setHasMore(true);
    setAllPillItems([]);
    setPillItems([]);

    if (selectedOption.value !== 'SELECTED') {
      fetchFields('', 0, selectedOption.value);
    } else {
      const selectedPills = allPillItems.filter((pill) =>
        selectedFields.some((f) => f.id === pill.id)
      );
      setPillItems(selectedPills);
    }
  };

  return (
    <div className="FieldsList">
      <div className='FieldsList__header'>
        <div className='FieldsList__search'>
          <TextInput
            testId={`am-v2-space-assets-filters-fileds-search`}
            onChange={handleChange}
            placeholder={`Search within ${filterView.value.toLocaleLowerCase()} Fields...`}
            type="text"
            version="v2"
            width="large"
            value={searchText}
            willBlurOnEsc
            prefix={
              <><Icon icon='Search' size='medium'></Icon></>
            }
          />
        </div>
        <div className='FieldsList__display-dropdown'>
          <Select
            version='v2'
            options={FIELDS_LIST_FILTERS_OPTIONS}
            value={filterView}
            isSearchable={false}
            onChange={(e) => {
              handleViewchange(e);
            }}
          ></Select>
        </div>
      </div>
      <div className="FieldsList__pill-container" onScroll={handleScroll}>
        {selectedFields.length >= 50 &&
          <div className='FieldsList__validation-banner'>
            <Info
              testId='am-v2-space-assets-filters-info'
              version='v2'
              content={<>
                <b>{'Selection Limit Reached:'}</b> {'You can only select up to 50 Fields. Deselect a field to choose a different one.'}
              </>}
              borderColor='#EB5646'
              backgroundColor='#FFF8EB'
              style={{ height: '2rem' }}
            >
            </Info>
          </div>
        }
        {loading && pillItems.length === 0 ? (
          <LoadingSpinner color="grey" />
        ) : (
          <Pills
            items={pillItems.map((pill) => ({
              ...pill,
              text: typeof pill.text === 'string' ? pill.text : String(pill.text ?? ''), // ensure string
              isSelected: selectedFields.some((f) => f.id === pill.id),
            }))}
            highlightText={typeof searchText === 'string' ? searchText : ''}
            isError={false}
            variant={'label'}
            status={'default'}
            shouldHaveBorder={false}
            onPillClickHandler={togglePillSelection}
            canTruncate={true}
            truncateOptions={{
              truncateFrom: 'middle',
              maxChar: 25,
              isResponsive: false,
              tooltip: {
                position: 'right',
                showArrow: true,
                variantType: 'dark',
              },
              delimiter: '/',
              children: null,
            }}
          />
        )}
      </div>

      {allPillItems.length > 10000 && <div className="FieldsList__pill-note">
        <p>{'There are over'} <b>10000+</b> {'fields. Try refining your search to see more relevant results'}</p>
      </div>}
      <div className="FieldsList__pill-actions">
        <div className="ActionButtons">
          <div className="ActionButtons__left">
            <Button buttonType="tertiary" onClick={closeDropdown}>{'Cancel'}</Button>
          </div>
          <div className="ActionButtons__right">
            <Button buttonType="secondary" onClick={clearAll}>{'Clear All Selection'}</Button>
            <Button buttonType="primary" onClick={applySelectedFields} disabled={selectedFields.length >= 50}>
              {'Apply Selection'} {selectedFields.length > 0 ? `(${selectedFields.length})` : ''}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );

}

export default FieldsList